import React from 'react';
import { ocrRawValues } from '../../data/sampleCase';
import type { CaseFieldKey } from '../../types/notice';
interface ScannedNoticeProps {
  activeField?: CaseFieldKey | null;
  lowFields?: CaseFieldKey[];
  scanning?: boolean;
  onFieldClick?: (k: CaseFieldKey) => void;
}

/** Imitation of the current plain DMP printed prosecution notice, as a scanned copy. */
export function ScannedNotice({
  activeField,
  lowFields = [],
  scanning = false,
  onFieldClick
}: ScannedNoticeProps) {
  const d = ocrRawValues;
  const F = ({
    k,
    children
  }: {
    k: CaseFieldKey;
    children?: React.ReactNode;
  }) => {
    const active = activeField === k;
    const low = lowFields.includes(k);
    return <span onClick={onFieldClick ? () => onFieldClick(k) : undefined} className={`rounded-sm px-0.5 transition-colors ${onFieldClick ? 'cursor-pointer' : ''} ${active ? 'bg-[#1769AA]/20 outline outline-2 outline-[#1769AA]' : low ? 'bg-[#E69A20]/25 outline outline-1 outline-dashed outline-[#E69A20]' : ''}`} data-id="element-225" data-mp="2z20df2zzspnb">
        {children ?? d[k]}
      </span>;
  };
  return <div className="scan-paper relative overflow-hidden font-serifbn text-[15px] leading-[1.75] text-[#222]" style={{
    width: 794,
    minHeight: 1050,
    padding: '56px 64px',
    transform: 'rotate(-0.35deg)',
    filter: 'contrast(0.96)'
  }} data-id="element-226" data-mp="2efwdneirzkwm">
      <div className="absolute right-6 top-4 rounded border border-slate-400 px-2 py-0.5 font-sans text-[11px] text-slate-500" data-id="element-227" data-mp="2obq0bqq7kus4">স্ক্যান কপি • পৃষ্ঠা ১/১</div>
      <div className="text-center" data-id="element-228" data-mp="2x64axfzsnh4w">
        <div className="text-[15px]" data-id="element-229" data-mp="2x52zvbis2un6">গণপ্রজাতন্ত্রী বাংলাদেশ সরকার</div>
        <div className="text-[19px] font-bold" data-id="element-230" data-mp="3c354zq5gjo6j">ঢাকা মেট্রোপলিটন পুলিশ</div>
        <div className="text-[15px]" data-id="element-231" data-mp="373sgz22uob2d">ট্রাফিক বিভাগ (তেজগাঁও), ঢাকা</div>
      </div>
      <div className="mt-6 flex justify-between" data-id="element-232" data-mp="371xxls788r0e">
        <span data-id="element-233" data-mp="1hpvq1iu57agw">স্মারক নং-৬৪১/১৭৭১</span>
        <span data-id="element-234" data-mp="1hqyivwb0zmzg">তারিখ: <F k="caseDate" data-id="element-235" data-mp="1hqozirkhke2q" /></span>
      </div>
      <div className="mt-5" data-id="element-236" data-mp="1cqsz12azz4rx">
        <div data-id="element-237" data-mp="1crluh74ia7ur">প্রাপক,</div>
        <div className="pl-6" data-id="element-238" data-mp="22dwpgrfaby4z">
          <div data-id="element-239" data-mp="22eone8wcu7yf"><F k="owner" data-id="element-240" data-mp="22eoibuhhzhrk" /></div>
          <div data-id="element-241" data-mp="22fpxaa1mwoek"><F k="address" data-id="element-242" data-mp="22fpww8xq2me1" /></div>
          <div data-id="element-243" data-mp="26f8i75tggzhe">মোবাইল: <F k="mobile" data-id="element-244" data-mp="26eyphah10e80" /></div>
        </div>
      </div>
      <div className="mt-5" data-id="element-245" data-mp="26hkxfb7twvqr">
        <span className="font-bold" data-id="element-246" data-mp="2beuv1e5dgdpo">বিষয়: </span>সড়ক পরিবহন আইন, ২০১৮ এর ১১৮ ধারা মোতাবেক চালক/মালিক উপস্থিত হওয়া প্রসঙ্গে।
      </div>
      <p className="mt-4 text-justify indent-10" data-id="element-247" data-mp="2gepp1qkg1jey">
        উপর্যুক্ত বিষয়ের প্রেক্ষিতে জানানো যাচ্ছে যে, ডিএমপির AI ক্যামেরার মাধ্যমে <F k="caseDate" data-id="element-248" data-mp="2lciswcy9y1c2" /> তারিখ <F k="caseTime" data-id="element-249" data-mp="2pecq2b99gn7h" /> সময়ে <F k="location" data-id="element-250" data-mp="2pduhcf5ewgio" /> এলাকায় আপনার মালিকানাধীন গাড়ি নং <F k="vehicle" data-id="element-251" data-mp="2pc9oqv2nr7ng" /> এর বিরুদ্ধে <F k="offence" data-id="element-252" data-mp="2u5cl1aytkv7c" /> এর অপরাধে <F k="law" data-id="element-253" data-mp="2u4t51zsqcgvy" /> মোতাবেক মামলা নং <F k="caseNo" data-id="element-254" data-mp="2u5uqmy7dglrx" /> রুজু করা হয়েছে।
      </p>
      <p className="mt-3 text-justify indent-10" data-id="element-255" data-mp="2z4p6psdyfw8c">
        এমতাবস্থায়, আগামী <F k="hearingDate" data-id="element-256" data-mp="2z60b9uf8580i" /> তারিখ <F k="hearingTime" data-id="element-257" data-mp="343sdpidhwciy" /> ঘটিকায় গাড়ির প্রয়োজনীয় কাগজপত্রসহ নিম্নস্বাক্ষরকারীর কার্যালয়ে উপস্থিত হওয়ার জন্য অনুরোধ করা হলো।
      </p>
      <div className="mt-16 flex justify-end" data-id="element-258" data-mp="392v6o4g7asak">
        <div className="w-64 text-center" data-id="element-259" data-mp="xeqe4riglh2m">
          <svg viewBox="0 0 200 50" className="mx-auto h-12 w-44" aria-hidden data-id="element-260" data-mp="xfrosuvkc1jt">
            <path d="M8 34c18-22 28 10 42-6s14-16 24 2 20-14 32-4 12 14 30 0 26-8 52-2" fill="none" stroke="#1f3b8a" strokeWidth="2.2" strokeLinecap="round" data-id="element-261" data-mp="sfng3r0wcb45" />
          </svg>
          <div className="border-t border-dashed border-slate-500 pt-1" data-id="element-262" data-mp="ihza24xubh6o">উপ-পুলিশ কমিশনার</div>
          <div data-id="element-263" data-mp="1g5hrs59hbtmt">ট্রাফিক বিভাগ (তেজগাঁও)</div>
          <div data-id="element-264" data-mp="1g46g7j9slhl5">ডিএমপি, ঢাকা</div>
        </div>
      </div>
      <div className="absolute bottom-16 left-16 h-20 w-20 rotate-[-12deg] rounded-full border-2 border-[#5b4fc4]/50 text-center text-[10px] leading-tight text-[#5b4fc4]/60" data-id="element-265" data-mp="1b6ofgg5xi8o8">
        <div className="mt-6" data-id="element-266" data-mp="16dlmoa8z7393">ডিএমপি</div>
        <div data-id="element-267" data-mp="11hd9h2ec9k4k">ট্রাফিক</div>
      </div>
      {scanning && <div className="pointer-events-none absolute inset-0 bg-[#1769AA]/5" data-id="element-268" data-mp="11er2b3vd16mv">
          <div className="scan-line absolute left-0 right-0 h-1 bg-[#1769AA] shadow-[0_0_24px_6px_rgba(23,105,170,0.45)]" data-id="element-269" data-mp="1yxab17yqy0hc" />
        </div>}
    </div>;
}